import axios from 'axios'
import serverUrl from '@/assets/BaseServerUrl.js'
import webSocketService from './websocketService'

/**
 * 后台 AI 翻译客户端。
 *
 * 提交后立刻返回，不再让页面阻塞等待大模型；结果通过 socket 定向推送，
 * 页面切走/组件卸载后任务继续跑，回来用 peek() 恢复进度与译文。
 * socket 断线时由轮询兜底。
 */

const POLL_INTERVAL = 3000;
const MAX_POLL_MS = 20 * 60 * 1000;

const jobs = new Map();
const jobsByTextId = new Map();
// 组件通常「先订阅、再提交」，此时 job 还不存在，先挂到这里等创建后补交
const pendingSubs = new Map();

const jobKeyOf = ({ type, targetId }) => `${type || 'general'}::${targetId || 'none'}`;

function createSnapshot(job) {
  return {
    status: job.status,
    progress: job.progress,
    textId: job.textId,
    taskId: job.taskId,
    translatedText: job.translatedText,
    costPoints: job.costPoints,
    error: job.error,
    cached: job.cached
  };
}

function notify(job) {
  job.listeners.forEach((fn) => {
    try { fn(createSnapshot(job)); } catch (err) { console.error('翻译进度回调异常:', err) }
  });
}

function stopPolling(job) {
  if (job.pollTimer) { clearInterval(job.pollTimer); job.pollTimer = null; }
}

function release(job) {
  stopPolling(job);
  jobsByTextId.delete(job.textId);
  jobs.delete(job.key);
}

/**
 * 把服务端返回的任务状态同步进本地 job
 */
function applyStatus(job, payload) {
  if (!payload) return;
  if (typeof payload.progress === 'number') job.progress = payload.progress;
  if (payload.status) job.status = payload.status;
  if (payload.textId) {
    job.textId = payload.textId;
    jobsByTextId.set(job.textId, job);
  }
  if (payload.taskId) job.taskId = payload.taskId;
  if (payload.costPoints !== undefined) job.costPoints = payload.costPoints;
}

function succeed(job, translatedText, cached = false) {
  job.status = 'completed';
  job.progress = 100;
  job.translatedText = translatedText;
  job.cached = cached;
  stopPolling(job);
  notify(job);
  job.resolve(translatedText);
  // 保留已完成的 job 以便页面回显译文，只清理轮询与索引
  jobsByTextId.delete(job.textId);
}

function fail(job, message) {
  job.status = 'failed';
  job.error = message || '翻译失败';
  stopPolling(job);
  notify(job);
  job.reject(new Error(job.error));
  release(job);
}

async function pollOnce(job) {
  if (!job.textId) return;
  const res = await axios.get(`${serverUrl}/api/translate/query`, { params: { textId: job.textId } });
  const data = res.data || {};

  if (data.status === 'completed' && data.translatedText) {
    succeed(job, data.translatedText, !!data.cached);
    return;
  }
  if (data.status === 'failed') {
    fail(job, data.error || '后台翻译失败，积分已退还');
    return;
  }
  if (data.status === 'not_found') {
    // 任务被服务重启回收，或从未登记
    fail(job, data.message || '翻译任务已失效，请重新发起');
    return;
  }
  applyStatus(job, data);
  notify(job);
}

function startPolling(job) {
  if (job.pollTimer) return;
  const deadline = Date.now() + MAX_POLL_MS;
  job.pollTimer = setInterval(() => {
    if (Date.now() > deadline) {
      fail(job, '翻译超时，请稍后重试');
      return;
    }
    pollOnce(job).catch((err) => {
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        fail(job, err?.response?.data?.message || '登录状态已失效，请重新登录');
        return;
      }
      // 网络抖动：下个周期再试
      console.warn('查询翻译任务失败，稍后重试:', err?.message);
    });
  }, POLL_INTERVAL);
}

/**
 * 查看当前翻译任务快照（页面重进时用于恢复 UI 状态）
 */
export function peek(opts) {
  return jobs.get(jobKeyOf(opts)) ? createSnapshot(jobs.get(jobKeyOf(opts))) : null;
}

/**
 * 订阅任务进度，返回取消订阅函数。
 * 允许在任务提交前订阅（组件常见时序），此时先登记，job 创建后立即补挂。
 */
export function subscribe(opts, listener) {
  const key = jobKeyOf(opts);
  const job = jobs.get(key);
  if (job) {
    job.listeners.add(listener);
    listener(createSnapshot(job));
    return () => job.listeners.delete(listener);
  }

  if (!pendingSubs.has(key)) pendingSubs.set(key, new Set());
  pendingSubs.get(key).add(listener);
  return () => {
    const set = pendingSubs.get(key);
    if (!set) return;
    set.delete(listener);
    if (!set.size) pendingSubs.delete(key);
  };
}

/**
 * 发起（或复用正在进行的）后台翻译，Promise 解析为中文译文。
 * 进度请通过 subscribe() 订阅，以便组件卸载时能干净地退订。
 */
export async function requestTranslate(opts) {
  const { text, type = 'readme', targetId = '', targetTitle = '' } = opts || {};
  if (!text || !text.trim()) throw new Error('翻译内容为空');

  const key = jobKeyOf({ type, targetId });
  const existing = jobs.get(key);

  if (existing) {
    if (existing.status === 'completed') return existing.translatedText;
    return existing.promise;
  }

  let resolveFn; let rejectFn;
  const promise = new Promise((resolve, reject) => { resolveFn = resolve; rejectFn = reject });
  // 组件可能只订阅不等待，先挂一个空 catch 防未处理的 promise rejection
  promise.catch(() => {});

  const job = {
    key,
    text,
    type,
    targetId,
    targetTitle,
    status: 'queued',
    progress: 0,
    textId: '',
    taskId: '',
    translatedText: null,
    costPoints: 0,
    error: null,
    cached: false,
    listeners: new Set(),
    pollTimer: null,
    resolve: resolveFn,
    reject: rejectFn,
    promise
  };
  jobs.set(key, job);

  const pending = pendingSubs.get(key);
  if (pending) {
    pending.forEach((fn) => job.listeners.add(fn));
    pendingSubs.delete(key);
    // 补发一次当前快照，确保订阅者不会漏掉已经发生的状态变更
    notify(job);
  }

  try {
    const res = await axios.post(`${serverUrl}/api/translate/async`, {
      text,
      type,
      targetId,
      targetTitle
    });
    const data = res.data || {};

    if (!data.success) {
      fail(job, data.message || '后台翻译任务提交失败');
      return promise;
    }

    applyStatus(job, data);
    job.costPoints = data.costPoints || job.costPoints;

    if (data.status === 'completed' && data.translatedText) {
      succeed(job, data.translatedText, !!data.cached);
      return promise;
    }

    job.status = data.status || 'processing';
    notify(job);
    startPolling(job);
  } catch (err) {
    fail(job, err?.response?.data?.message || err?.message || '后台翻译任务提交失败');
  }

  return promise;
}

/** 主动放弃任务（例如用户清空），不影响服务端继续处理 */
export function abort(opts) {
  const key = jobKeyOf(opts);
  pendingSubs.delete(key);
  const job = jobs.get(key);
  if (!job) return;
  stopPolling(job);
  jobsByTextId.delete(job.textId);
  jobs.delete(key);
}

// ---------- socket 事件分发（模块加载时注册一次） ----------

function findJobByTextId(textId) {
  if (!textId) return null;
  const direct = jobsByTextId.get(textId);
  if (direct) return direct;
  for (const job of jobs.values()) {
    if (job.textId === textId) return job;
  }
  return null;
}

function handleSocket(payload, onSettled) {
  const job = findJobByTextId(payload?.textId);
  if (!job) return;
  applyStatus(job, payload);
  onSettled(job, payload);
}

webSocketService.on('translateProgress', (payload) => {
  handleSocket(payload, (job) => notify(job));
});

webSocketService.on('translateCompleted', (payload) => {
  handleSocket(payload, (job, data) => succeed(job, data.translatedText, false));
});

webSocketService.on('translateFailed', (payload) => {
  handleSocket(payload, (job, data) => fail(job, data.error || '后台翻译失败，积分已退还'));
});

export default { requestTranslate, peek, subscribe, abort };
